import { useState, useEffect, useRef } from 'preact/hooks';
import { Volume2, AlertCircle, Sparkles, Loader2, ImageOff, Smile, FileText, ExternalLink, RefreshCw } from 'lucide-preact';
import type { Message } from '../../types/message';
import { apiFetch } from '../../services/api';
import { showToast } from '../../state/ui.store';
import { activeContactSignal, loadContactDetails } from '../../state/contacts.store';

interface MediaAttachmentProps {
  message: Message;
  onOpenVisionModal?: (mediaUrl: string, content: string, messageId?: string) => void;
}

// Global in-memory caches to prevent repeated network requests during scroll & re-renders
const mediaUrlCache = new Map<string, string>();
const failedMediaCache = new Set<string>();
const inFlightMediaFetches = new Map<string, Promise<{ mediaUrl?: string; base64?: string; mimetype?: string; expired?: boolean; error?: string }>>();

export function clearMediaCache() {
  mediaUrlCache.clear();
  failedMediaCache.clear();
  inFlightMediaFetches.clear();
}

export function getValidTranscript(text?: string | null): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/^\[.*(expirad|indispon|falha|erro).*\]$/i.test(trimmed)) {
    return null;
  }
  return trimmed;
}

export function openPdfInNewTab(urlOrBase64: string, filename?: string) {
  try {
    if (!urlOrBase64) return;

    let base64 = urlOrBase64;
    let mimeType = 'application/pdf';

    if (urlOrBase64.startsWith('data:')) {
      const parts = urlOrBase64.split(';base64,');
      mimeType = parts[0].replace('data:', '') || 'application/pdf';
      base64 = parts[1];
    }

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank');
    if (win) {
      win.focus();
    }
  } catch (e) {
    console.warn('[PDF] Fallback para window.open direto:', e);
    const win = window.open(urlOrBase64, '_blank');
    if (win) win.focus();
  }
}

export function MediaAttachment({ message, onOpenVisionModal }: MediaAttachmentProps) {
  const rawContent = message.content || '';
  const actualBody = rawContent.replace(/^>\s*\[\[@[\s\S]+?\]\]:\s*[\s\S]+?(?:\n\n|\n(?=[^\n]))/, '').trim();
  const contentUpper = actualBody.toUpperCase();

  const isImage = message.message_type === 'image' || (Boolean(actualBody) && (contentUpper === '[IMAGEM]' || contentUpper.startsWith('[IMAGEM]')));
  const isSticker = message.message_type === 'sticker' || (Boolean(actualBody) && (contentUpper === '[FIGURINHA]' || contentUpper === '[STICKER]'));
  const isAudio = message.message_type === 'audio' || (Boolean(actualBody) && (contentUpper === '[ÁUDIO]' || contentUpper === '[AUDIO]'));
  const isDocument = message.message_type === 'document' ||
    (message.mime_type && message.mime_type.includes('pdf')) ||
    (Boolean(actualBody) && (contentUpper.includes('[DOCUMENTO]') || contentUpper.endsWith('.PDF')));

  const currentId = message.message_id;
  const initialUrl = message.media_url || mediaUrlCache.get(currentId) || null;
  const isKnownFailed = failedMediaCache.has(currentId) || Boolean(message.is_expired);

  const [mediaUrl, setMediaUrl] = useState<string | null>(initialUrl);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(isKnownFailed);
  const [retrying, setRetrying] = useState(false);
  const fetchingRef = useRef<string | null>(null);

  const handleForceRetry = async (e?: Event) => {
    if (e) e.stopPropagation();
    if (retrying) return;
    try {
      setRetrying(true);
      failedMediaCache.delete(currentId);
      mediaUrlCache.delete(currentId);
      setMediaError(false);
      setLoadingMedia(true);

      const res = await apiFetch<any>(`/messages/${currentId}/retry-media`, { method: 'POST' });

      if (res?.success && (res?.mediaUrl || res?.base64)) {
        const url = res.mediaUrl || `data:${res.mimetype || 'audio/ogg'};base64,${res.base64}`;
        mediaUrlCache.set(currentId, url);
        setMediaUrl(url);
        setMediaError(false);
        showToast('Mídia e transcrição recuperadas do WhatsApp com sucesso!', 'success');

        if (activeContactSignal.value) {
          loadContactDetails(activeContactSignal.value);
        }
      } else {
        failedMediaCache.add(currentId);
        setMediaError(true);
        showToast(res?.error || 'O WhatsApp indicou que este arquivo de mídia expirou.', 'warning');
      }
    } catch (err: any) {
      failedMediaCache.add(currentId);
      setMediaError(true);
      showToast(`Falha ao tentar baixar do WhatsApp: ${err.message || 'Erro de conexão'}`, 'error');
    } finally {
      setRetrying(false);
      setLoadingMedia(false);
    }
  };

  useEffect(() => {
    // 1. If already has URL from prop or cache, set and stop
    const cachedUrl = message.media_url || mediaUrlCache.get(currentId) || null;
    if (cachedUrl) {
      setMediaUrl(cachedUrl);
      setMediaError(false);
      setLoadingMedia(false);
      return;
    }

    // 2. If already known to be failed or expired, set error and stop
    if (failedMediaCache.has(currentId) || message.is_expired) {
      setMediaError(true);
      setLoadingMedia(false);
      return;
    }

    // 3. Only fetch for media types that require fetching
    if (!isImage && !isSticker && !isAudio && !isDocument) return;

    // 4. Prevent duplicate triggers within component instance
    if (fetchingRef.current === currentId) return;
    fetchingRef.current = currentId;

    let cancelled = false;
    setLoadingMedia(true);

    // 5. Deduplicate across component instances if a fetch is already in-flight
    let fetchPromise = inFlightMediaFetches.get(currentId);
    if (!fetchPromise) {
      fetchPromise = apiFetch<{ mediaUrl?: string; base64?: string; mimetype?: string; expired?: boolean; error?: string }>(`/messages/media/${currentId}`);
      inFlightMediaFetches.set(currentId, fetchPromise);
    }

    fetchPromise
      .then(res => {
        inFlightMediaFetches.delete(currentId);
        if (cancelled) return;

        const resolvedUrl = res?.mediaUrl || (res?.base64 ? `data:${res.mimetype || (isDocument ? 'application/pdf' : 'image/png')};base64,${res.base64}` : null);

        if (resolvedUrl) {
          mediaUrlCache.set(currentId, resolvedUrl);
          setMediaUrl(resolvedUrl);
          setMediaError(false);
        } else if (res?.expired || res?.error) {
          failedMediaCache.add(currentId);
          setMediaError(true);
        } else {
          failedMediaCache.add(currentId);
          setMediaError(true);
        }
      })
      .catch(err => {
        inFlightMediaFetches.delete(currentId);
        if (cancelled) return;
        failedMediaCache.add(currentId);
        setMediaError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMedia(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [message.message_id, message.media_url, isImage, isSticker, isAudio, isDocument]);

  if (isDocument) {
    const rawContent = message.content || '';
    const docTitle = rawContent && !rawContent.startsWith('[Documento')
      ? rawContent
      : 'Documento Anexo (PDF)';
    const validTranscript = getValidTranscript(message.extracted_text);

    const handleOpenPdf = () => {
      if (mediaUrl) {
        openPdfInNewTab(mediaUrl, docTitle);
      } else if (!loadingMedia) {
        setLoadingMedia(true);
        apiFetch<{ mediaUrl?: string; base64?: string; mimetype?: string }>(`/messages/media/${currentId}`)
          .then(res => {
            const url = res?.mediaUrl || (res?.base64 ? `data:${res.mimetype || 'application/pdf'};base64,${res.base64}` : null);
            if (url) {
              mediaUrlCache.set(currentId, url);
              setMediaUrl(url);
              openPdfInNewTab(url, docTitle);
            }
          })
          .catch(() => setMediaError(true))
          .finally(() => setLoadingMedia(false));
      }
    };

    return (
      <div class="my-1.5 rounded-2xl border border-slate-800/80 bg-slate-950/90 overflow-hidden max-w-sm group shadow-md hover:border-cyan-500/40 transition-all select-none">
        {/* Subtle Translucent Overlay Bar */}
        <div
          onClick={handleOpenPdf}
          class="px-3 py-2 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/60 flex items-center justify-between gap-2.5 cursor-pointer"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 flex-shrink-0">
              PDF
            </span>
            <span class="text-xs font-semibold text-slate-100 truncate group-hover:text-cyan-300 transition-colors">
              {docTitle}
            </span>
          </div>

          <div class="w-6 h-6 rounded-md bg-slate-800/80 group-hover:bg-cyan-500/20 text-slate-400 group-hover:text-cyan-300 flex items-center justify-center transition-colors flex-shrink-0">
            {loadingMedia ? (
              <Loader2 class="w-3 h-3 animate-spin text-cyan-400" />
            ) : (
              <ExternalLink class="w-3 h-3" />
            )}
          </div>
        </div>

        {/* Thumbnail Preview Area */}
        <div
          onClick={handleOpenPdf}
          class="relative w-full h-36 bg-slate-900/40 overflow-hidden cursor-pointer flex items-center justify-center"
        >
          {mediaUrl ? (
            <object
              data={`${mediaUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              class="w-full h-full pointer-events-none opacity-85 group-hover:opacity-100 transition-opacity"
            >
              <div class="flex flex-col items-center justify-center p-4 text-slate-400 gap-1">
                <FileText class="w-6 h-6 text-rose-400/80" />
                <span class="text-[10px] font-mono">Visualizar PDF</span>
              </div>
            </object>
          ) : (
            <div class="flex flex-col items-center justify-center text-slate-400 gap-1.5 p-4">
              {loadingMedia ? (
                <Loader2 class="w-5 h-5 animate-spin text-cyan-400" />
              ) : (
                <FileText class="w-7 h-7 text-rose-400/80" />
              )}
              <span class="text-[10px] font-mono text-slate-400">
                {loadingMedia ? 'Carregando PDF...' : 'Clique para abrir PDF'}
              </span>
            </div>
          )}

          {/* Subtle Hover Overlay */}
          <div class="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
            <span class="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-cyan-500/40 text-cyan-300 text-[10px] font-bold shadow-xl flex items-center gap-1.5 transition-transform group-hover:scale-105">
              <ExternalLink class="w-3 h-3 text-cyan-400" /> Abrir PDF em Nova Aba
            </span>
          </div>
        </div>

        {/* Transcript Section if available */}
        {validTranscript && (
          <div class="p-2.5 bg-slate-900/80 border-t border-slate-800 space-y-1">
            <div class="flex items-center gap-1.5 text-[11px] font-semibold text-cyan-400 tracking-wide uppercase">
              <Sparkles class="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
              <span>Resumo do Documento</span>
            </div>
            <p class="text-xs leading-relaxed text-slate-200 italic">{validTranscript}</p>
          </div>
        )}
      </div>
    );
  }

  if (isImage) {
    const validTranscript = getValidTranscript(message.extracted_text);

    if (mediaError || message.is_expired) {
      return (
        <div class="my-1.5 p-3 bg-slate-950/90 border border-slate-800 rounded-2xl space-y-2 max-w-sm">
          <div class="flex items-start gap-2.5 p-2.5 bg-rose-950/30 border border-rose-500/25 rounded-xl text-rose-300">
            <ImageOff class="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
            <div class="text-xs space-y-0.5">
              <p class="font-semibold text-rose-200">Imagem Indisponível</p>
              <p class="text-[11px] text-rose-400/80 leading-normal">
                A URL temporária desta imagem expirou no WhatsApp.
              </p>
            </div>
          </div>
          {validTranscript && (
            <div class="p-2.5 bg-slate-900/90 border border-slate-800 rounded-xl space-y-1">
              <div class="flex items-center gap-1.5 text-[11px] font-semibold text-cyan-400 tracking-wide uppercase">
                <Sparkles class="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                <span>Análise Vision</span>
              </div>
              <p class="text-xs leading-relaxed text-slate-200 italic">{validTranscript}</p>
            </div>
          )}
        </div>
      );
    }

    if (loadingMedia && !mediaUrl) {
      return (
        <div class="p-3 bg-slate-950/80 border border-slate-800 rounded-xl text-[11px] text-cyan-400 font-mono animate-pulse flex items-center gap-2">
          <Loader2 class="w-3.5 h-3.5 animate-spin" />
          <span>Carregando imagem do WhatsApp...</span>
        </div>
      );
    }

    return (
      <div class="relative group my-1.5 overflow-hidden rounded-xl border border-slate-800 max-w-sm">
        <img
          src={mediaUrl || ''}
          alt="Imagem da conversa"
          referrerPolicy="no-referrer"
          onError={() => setMediaError(true)}
          class="w-full h-auto max-h-80 object-cover rounded-xl cursor-pointer transition transform group-hover:scale-105"
          onClick={() => {
            if (mediaUrl && onOpenVisionModal) {
              onOpenVisionModal(mediaUrl, message.content, message.message_id);
            }
          }}
        />
        <div class="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition">
          <button
            onClick={() => {
              if (mediaUrl && onOpenVisionModal) {
                onOpenVisionModal(mediaUrl, message.content, message.message_id);
              }
            }}
            class="px-2.5 py-1 bg-slate-950/80 hover:bg-cyan-950 border border-cyan-500/40 text-cyan-300 text-[10px] font-bold rounded-lg backdrop-blur-md shadow-lg cursor-pointer"
          >
            🔍 Analisar com Vision
          </button>
        </div>
      </div>
    );
  }

  if (isSticker) {
    if (mediaError || message.is_expired) {
      return (
        <div class="my-1.5 p-2 text-xs text-slate-400 font-mono italic flex items-center gap-1.5">
          <Smile class="w-3.5 h-3.5 text-slate-500" />
          <span>[Figurinha Indisponível]</span>
        </div>
      );
    }

    if (loadingMedia && !mediaUrl) {
      return (
        <div class="p-2 text-[11px] text-cyan-400 font-mono animate-pulse flex items-center gap-1.5">
          <Loader2 class="w-3.5 h-3.5 animate-spin" />
          <span>Carregando figurinha...</span>
        </div>
      );
    }

    return (
      <div class="my-1.5 overflow-hidden rounded-xl max-w-[160px]">
        <img
          src={mediaUrl || ''}
          alt="Figurinha"
          referrerPolicy="no-referrer"
          onError={() => setMediaError(true)}
          class="w-32 h-32 object-contain drop-shadow-md"
        />
      </div>
    );
  }

  if (isAudio) {
    const validTranscript = getValidTranscript(message.extracted_text);
    const isExpiredOrError = mediaError || message.is_expired;

    return (
      <div class="my-1.5 p-3 bg-slate-950/90 border border-slate-800 rounded-2xl space-y-2.5 max-w-md shadow-sm">
        {/* Audio Player / Media State */}
        {mediaUrl ? (
          <div class="flex items-center gap-2">
            <audio controls class="w-full h-8 accent-cyan-500" src={mediaUrl}>
              Seu navegador não suporta reprodução de áudio.
            </audio>
          </div>
        ) : loadingMedia ? (
          <div class="text-xs text-cyan-400 font-medium flex items-center gap-2 p-1.5 bg-cyan-950/30 border border-cyan-500/20 rounded-xl">
            <Loader2 class="w-4 h-4 animate-spin text-cyan-400 flex-shrink-0" />
            <span>Carregando áudio do WhatsApp...</span>
          </div>
        ) : isExpiredOrError ? (
          <div class="flex items-start justify-between gap-2.5 p-2.5 bg-amber-950/30 border border-amber-500/25 rounded-xl text-amber-300">
            <div class="flex items-start gap-2.5 min-w-0">
              <AlertCircle class="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div class="text-xs space-y-0.5 min-w-0">
                <p class="font-semibold text-amber-200">Áudio indisponível / expirado</p>
                <p class="text-[11px] text-amber-400/80 leading-normal">
                  {validTranscript
                    ? 'O arquivo de áudio expirou, mas a transcrição está salva abaixo.'
                    : 'Não foi possível carregar o arquivo de áudio automaticamente.'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleForceRetry}
              disabled={retrying}
              class="px-2.5 py-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 hover:text-amber-100 text-[10px] font-extrabold rounded-lg transition flex items-center gap-1 shrink-0 cursor-pointer disabled:opacity-50 shadow-sm"
              title="Tentar baixar o áudio novamente do WhatsApp (forçar re-download)"
            >
              <RefreshCw class={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} />
              <span>{retrying ? 'Baixando...' : 'Tentar Novamente'}</span>
            </button>
          </div>
        ) : (
          <div class="text-xs text-slate-400 italic p-1 flex items-center gap-2">
            <Volume2 class="w-4 h-4 text-slate-500" />
            <span>Aguardando áudio...</span>
          </div>
        )}

        {/* Transcript Section */}
        {validTranscript ? (
          <div class="p-3 bg-slate-900/90 border border-slate-800 rounded-xl space-y-1">
            <div class="flex items-center gap-1.5 text-[11px] font-semibold text-cyan-400 tracking-wide uppercase">
              <Sparkles class="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
              <span>Transcrição</span>
            </div>
            <p class="text-xs leading-relaxed text-slate-200 italic pl-0.5">{validTranscript}</p>
          </div>
        ) : !isExpiredOrError && !message.extracted_text ? (
          <div class="text-[11px] text-slate-400 flex items-center gap-1.5 px-1 py-0.5">
            <Loader2 class="w-3 h-3 animate-spin text-cyan-400/70" />
            <span>Transcrição em andamento...</span>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}
